import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function CapabilityDialog({ open, onClose, capability, officers, issues, policies, objectives }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'developed',
    system_tech_component: '',
    people_component: '',
    process_component: '',
    sponsor_email: '',
    linked_issue_ids: [],
    linked_policy_ids: [],
    linked_objective_id: '',
    target_completion_date: '',
    cost_estimate: '',
    vendor_partner: ''
  });

  useEffect(() => {
    if (capability) {
      setFormData({
        name: capability.name || '',
        description: capability.description || '',
        type: capability.type || 'developed',
        system_tech_component: capability.system_tech_component || '',
        people_component: capability.people_component || '',
        process_component: capability.process_component || '',
        sponsor_email: capability.sponsor_email || '',
        linked_issue_ids: capability.linked_issue_ids || [],
        linked_policy_ids: capability.linked_policy_ids || [],
        linked_objective_id: capability.linked_objective_id || '',
        target_completion_date: capability.target_completion_date || '',
        cost_estimate: capability.cost_estimate || '',
        vendor_partner: capability.vendor_partner || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        type: 'developed',
        system_tech_component: '',
        people_component: '',
        process_component: '',
        sponsor_email: '',
        linked_issue_ids: [],
        linked_policy_ids: [],
        linked_objective_id: '',
        target_completion_date: '',
        cost_estimate: '',
        vendor_partner: ''
      });
    }
  }, [capability]);

  const createCapabilityMutation = useMutation({
    mutationFn: async (data) => {
      const user = await base44.auth.me();
      return base44.entities.Capability.create({
        ...data,
        requested_by: user.email,
        requested_at: new Date().toISOString(),
        status: 'requested'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      onClose();
    }
  });

  const updateCapabilityMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Capability.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      onClose();
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Edit mode requires a persisted record (i.e. one with an id).
    // Callers may pass a partial `capability` object purely to prefill
    // linked_issue_ids on a NEW request — that path must not flip us into Edit mode.
    if (capability?.id) {
      updateCapabilityMutation.mutate({ id: capability.id, data: formData });
    } else {
      createCapabilityMutation.mutate(formData);
    }
  };

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="issues-capabilitydialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{capability?.id ? 'Edit Capability' : 'Request New Capability'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Capability Name *</Label>
            <Input data-testid="issues-capabilitydialog-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea data-testid="issues-capabilitydialog-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              rows={3}
              placeholder="What will this capability enable?"
            />
          </div>

          <div className="space-y-2">
            <Label>Type *</Label>
            <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
              <SelectTrigger className="bg-slate-950 border-slate-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contracted">Contracted (External)</SelectItem>
                <SelectItem value="acquired">Acquired (Purchase/License)</SelectItem>
                <SelectItem value="developed">Developed (Build In-House)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4 p-4 bg-slate-950/50 border border-slate-800 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-300">Capability Components</h3>
            
            <div className="space-y-2">
              <Label>System/Technology Component</Label>
              <Textarea data-testid="issues-capabilitydialog-textarea-1"
                value={formData.system_tech_component}
                onChange={(e) => setFormData({ ...formData, system_tech_component: e.target.value })}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                rows={2}
                placeholder="What systems, tools, or technology?"
              />
            </div>

            <div className="space-y-2">
              <Label>People Component</Label>
              <Textarea data-testid="issues-capabilitydialog-textarea-2"
                value={formData.people_component}
                onChange={(e) => setFormData({ ...formData, people_component: e.target.value })}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                rows={2}
                placeholder="What skills, roles, or training?"
              />
            </div>

            <div className="space-y-2">
              <Label>Process Component</Label>
              <Textarea data-testid="issues-capabilitydialog-textarea-3"
                value={formData.process_component}
                onChange={(e) => setFormData({ ...formData, process_component: e.target.value })}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                rows={2}
                placeholder="What processes or workflows?"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Sponsor</Label>
              <Select value={formData.sponsor_email} onValueChange={(value) => setFormData({ ...formData, sponsor_email: value })}>
                <SelectTrigger className="bg-slate-950 border-slate-800">
                  <SelectValue placeholder="Select sponsor..." />
                </SelectTrigger>
                <SelectContent>
                  {officers.map((officer) => (
                    <SelectItem key={officer.id} value={officer.user_email}>
                      {officer.callsign}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Target Completion Date</Label>
              <Input data-testid="issues-capabilitydialog-input-1"
                type="date"
                value={formData.target_completion_date}
                onChange={(e) => setFormData({ ...formData, target_completion_date: e.target.value })}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              />
            </div>
          </div>

          {formData.type === 'contracted' && (
            <div className="space-y-2">
              <Label>Vendor/Partner</Label>
              <Input data-testid="issues-capabilitydialog-input-2"
                value={formData.vendor_partner}
                onChange={(e) => setFormData({ ...formData, vendor_partner: e.target.value })}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                placeholder="Name of vendor or partner"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Cost Estimate</Label>
            <Input data-testid="issues-capabilitydialog-input-3"
              value={formData.cost_estimate}
              onChange={(e) => setFormData({ ...formData, cost_estimate: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              placeholder="Time, money, resources needed"
            />
          </div>

          <div className="space-y-2">
            <Label>Link to Objective</Label>
            <Select value={formData.linked_objective_id} onValueChange={(value) => setFormData({ ...formData, linked_objective_id: value })}>
              <SelectTrigger className="bg-slate-950 border-slate-800">
                <SelectValue placeholder="Select objective..." />
              </SelectTrigger>
              <SelectContent>
                {objectives.map((objective) => (
                  <SelectItem key={objective.id} value={objective.id}>
                    [{objective.objective_code}] {objective.statement.substring(0, 60)}...
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Addresses Issues</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start bg-slate-950 border-slate-800">
                  {formData.linked_issue_ids.length === 0 ? 'Select issues...' : `${formData.linked_issue_ids.length} selected`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0 bg-slate-900 border-slate-800">
                <Command>
                  <CommandInput placeholder="Search issues..." />
                  <CommandList>
                    <CommandEmpty>No issues found.</CommandEmpty>
                    <CommandGroup>
                      {issues.map((issue) => (
                        <CommandItem
                          key={issue.id}
                          onSelect={() => {
                            setFormData({
                              ...formData,
                              linked_issue_ids: formData.linked_issue_ids.includes(issue.id)
                                ? formData.linked_issue_ids.filter((id) => id !== issue.id)
                                : [...formData.linked_issue_ids, issue.id]
                            });
                          }}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <div className={`w-4 h-4 border rounded ${formData.linked_issue_ids.includes(issue.id) ? 'bg-indigo-600 border-indigo-600' : 'border-slate-600'}`} />
                            <span className="flex-1">{issue.title}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {formData.linked_issue_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.linked_issue_ids.map((issueId) => {
                  const issue = issues.find((i) => i.id === issueId);
                  return (
                    <Badge key={issueId} variant="secondary" className="bg-red-500/10 text-red-400">
                      {issue?.title}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          linked_issue_ids: formData.linked_issue_ids.filter((id) => id !== issueId)
                        })}
                        className="ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Will Replace Policies</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start bg-slate-950 border-slate-800">
                  {formData.linked_policy_ids.length === 0 ? 'Select policies...' : `${formData.linked_policy_ids.length} selected`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0 bg-slate-900 border-slate-800">
                <Command>
                  <CommandInput placeholder="Search policies..." />
                  <CommandList>
                    <CommandEmpty>No policies found.</CommandEmpty>
                    <CommandGroup>
                      {policies.map((policy) => (
                        <CommandItem
                          key={policy.id}
                          onSelect={() => {
                            setFormData({
                              ...formData,
                              linked_policy_ids: formData.linked_policy_ids.includes(policy.id)
                                ? formData.linked_policy_ids.filter((id) => id !== policy.id)
                                : [...formData.linked_policy_ids, policy.id]
                            });
                          }}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <div className={`w-4 h-4 border rounded ${formData.linked_policy_ids.includes(policy.id) ? 'bg-indigo-600 border-indigo-600' : 'border-slate-600'}`} />
                            <span className="flex-1">{policy.title}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {formData.linked_policy_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.linked_policy_ids.map((policyId) => {
                  const policy = policies.find((p) => p.id === policyId);
                  return (
                    <Badge key={policyId} variant="secondary" className="bg-amber-500/10 text-amber-400">
                      {policy?.title}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          linked_policy_ids: formData.linked_policy_ids.filter((id) => id !== policyId)
                        })}
                        className="ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button data-testid="issues-cancel-btn" type="button" variant="outline" onClick={onClose} className="text-slate-900">
              Cancel
            </Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {capability?.id ? 'Update Capability' : 'Request Capability'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </ConfirmableDialog>
  );
}